import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { paymentAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { CheckCircle2, Loader2, XCircle, ArrowRight } from 'lucide-react';
import { trackPurchase, trackSubscribe } from '../../lib/metaPixel';

const PaymentSuccessPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { refreshUser } = useAuth();
    const [status, setStatus] = useState('loading');
    const [paymentDetails, setPaymentDetails] = useState(null);
    const [hasTrackedPurchase, setHasTrackedPurchase] = useState(false);
    const sessionId = searchParams.get('session_id');

    useEffect(() => {
        if (!sessionId) {
            setStatus('error');
            return;
        }

        const pollPaymentStatus = async (attempts = 0) => {
            const maxAttempts = 10;
            const pollInterval = 2000;

            if (attempts >= maxAttempts) {
                setStatus('timeout');
                return;
            }

            try {
                const response = await paymentAPI.getPaymentStatus(sessionId);
                setPaymentDetails(response.data);

                if (response.data.payment_status === 'paid') {
                    setStatus('success');
                    
                    // Track purchase in Meta Pixel (only once)
                    if (!hasTrackedPurchase) {
                        const paymentType = response.data.payment_type || 'subscription';
                        const amount = response.data.amount || 0;
                        const packageName = response.data.package_name || response.data.tier || 'Unknown';
                        
                        if (paymentType === 'subscription') {
                            trackSubscribe(packageName, amount);
                        }
                        trackPurchase(packageName, amount, paymentType);
                        setHasTrackedPurchase(true);
                    }
                    
                    // Refresh user to get updated subscription
                    await refreshUser();
                    return;
                } else if (response.data.status === 'expired') {
                    setStatus('expired');
                    return;
                }

                // Continue polling if still pending
                setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
            } catch (error) {
                console.error('Error checking payment status:', error);
                setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
            }
        };

        pollPaymentStatus();
    }, [sessionId, refreshUser, hasTrackedPurchase]);

    return (
        <Layout>
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="max-w-md w-full mx-auto px-4">
                    {status === 'loading' && (
                        <div className="text-center">
                            <Loader2 className="w-16 h-16 text-primary-600 animate-spin mx-auto mb-6" />
                            <h1 className="font-heading text-2xl font-bold text-neutral-900 mb-2">
                                Processing Payment...
                            </h1>
                            <p className="text-neutral-500">
                                Please wait while we confirm your payment
                            </p>
                        </div>
                    )}

                    {status === 'success' && (
                        <div className="text-center animate-fade-in">
                            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                                <CheckCircle2 className="w-10 h-10 text-emerald-600" />
                            </div>
                            <h1 className="font-heading text-2xl font-bold text-neutral-900 mb-2">
                                Payment Successful!
                            </h1>
                            <p className="text-neutral-500 mb-8">
                                Thank you for your purchase. Your account has been updated.
                            </p>
                            <div className="space-y-3">
                                <Button 
                                    className="w-full bg-primary-600 hover:bg-primary-700"
                                    onClick={() => navigate('/dashboard')}
                                    data-testid="go-dashboard-btn"
                                >
                                    Go to Dashboard
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                                <Button 
                                    variant="outline"
                                    className="w-full"
                                    asChild
                                >
                                    <Link to="/agencies">Find Recruiters</Link>
                                </Button>
                            </div>
                        </div>
                    )}

                    {(status === 'error' || status === 'expired' || status === 'timeout') && (
                        <div className="text-center animate-fade-in">
                            <div className="w-20 h-20 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-6">
                                <XCircle className="w-10 h-10 text-rose-600" />
                            </div>
                            <h1 className="font-heading text-2xl font-bold text-neutral-900 mb-2">
                                {status === 'expired' ? 'Payment Expired' : 'Payment Issue'}
                            </h1>
                            <p className="text-neutral-500 mb-8">
                                {status === 'expired' 
                                    ? 'Your payment session has expired. Please try again.'
                                    : status === 'timeout'
                                    ? 'We couldn\'t confirm your payment. Please check your email for confirmation.'
                                    : 'Something went wrong with your payment.'}
                            </p>
                            <div className="space-y-3">
                                <Button 
                                    className="w-full bg-primary-600 hover:bg-primary-700"
                                    onClick={() => navigate('/pricing')}
                                >
                                    Try Again
                                </Button>
                                <Button 
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => navigate('/dashboard')}
                                >
                                    Go to Dashboard
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default PaymentSuccessPage;
